import { computed } from 'vue';

import { toBirthDateStr } from '../../infrastructure/mappers/policyholderMapper';

/**
 * Resolves what a billing item only stores as a reference into what a worklist row shows.
 *
 * AN ITEM SNAPSHOTS NO NAMES, deliberately: a name is not an as-of fact the way a coverage or a
 * charge is, so it is read rather than frozen at projection time. Something therefore has to
 * resolve them, and the row presenter is TOLD rather than allowed to look them up — which is what
 * keeps it a pure mapping instead of something that reaches into stores.
 *
 * TOLD ITS SOURCES, for the same reason. The people come from a store outside this context and the
 * payers from the context's own loader, and the page already holds both, so injecting them leaves
 * the loading in one place and makes this testable without a store behind it.
 *
 * A RENDERING PROVIDER IS A PERSON, NOT A LOGIN, and that is why the provider names come from the
 * staff people rather than from the clinic's user accounts. An item's `renderingProviderId` is the
 * id of the person who delivered the service; resolving it against logins matches nothing at all,
 * and the presenter's id fallback then prints a raw id in a column that should read as a name.
 *
 * STAFF PEOPLE ARRIVE ALREADY SEPARATED, so this never asks what kind of record it is holding.
 * A person id that reached the provider column wrongly resolves to nothing and falls back to the
 * id, which is the honest failure; against every person it would resolve to a PATIENT's name in a
 * rendering-provider cell, which reads as fact and is the worse of the two by a distance.
 *
 * THE BIRTH DATE IS A TRANSLATION, not a lookup, and the only one here. The client document stores
 * an instant and a claim names a calendar date; the mapper owns that reduction and documents where
 * it is safe. Everything else below reads a field as stored.
 *
 * @param {Object} sources
 * @param {import('vue').Ref<Array>} sources.clients - the clinic's clients, as stored
 * @param {import('vue').Ref<Array>} sources.payers - one row per funding source, as it stands today
 * @param {import('vue').Ref<Array>} sources.staff - the clinic's staff people, as stored
 * @returns {import('vue').ComputedRef<Object>} the lookups `presentBillingItemRow` takes
 */
// eslint-disable-next-line import/prefer-default-export
export function useBillingItemLookups({ clients, payers, staff }) {
  // Maps rather than objects, so an id that happens to spell a prototype member misses instead of
  // resolving to a function, which is truthy and would be rendered as a name.
  const clientById = computed(() => new Map(
    (clients.value ?? []).map((client) => [client.id, client]),
  ));

  const payerNameById = computed(() => new Map(
    (payers.value ?? []).map((payer) => [payer.fundingSourceId, payer.name]),
  ));

  const providerNameById = computed(() => new Map(
    (staff.value ?? []).map((person) => [person.id, person.clientName]),
  ));

  return computed(() => ({
    clientName: (id) => clientById.value.get(id)?.clientName,
    clientDateOfBirth: (id) => toBirthDateStr(clientById.value.get(id)?.birthDate),
    payerName: (id) => payerNameById.value.get(id),
    providerName: (id) => providerNameById.value.get(id),
  }));
} // useBillingItemLookups()
